import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from 'react-redux';
import userActions from 'store/actions/users';
import employeeActions from 'store/actions/employees';
import clientActions from 'store/actions/clients';
import supplierActions from 'store/actions/suppliers';
import driverActions from 'store/actions/drivers';

const LATERAL_MENU_EVENT = 'lateralMenuChanged';

// El API solo acepta booleanos. Las preferencias antiguas o la sesión
// impersonada pueden no tener todavía un valor persistido.
const normalizeSidebarPinned = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  if (typeof value === 'number') return value === 1;
  return false;
};

// Mapa de tokens por perfil para verificar que existe sesión activa
const TOKEN_KEYS_BY_PROFILE = {
  user: '@token',
  employee: '@token',
  driver: '@driver_token',
  client: '@client_token',
  supplier: '@supplier_token',
  associate: '@super_admin_token',
};

export function useUniversalLateralMenuHook(profile = "user") {
  const dispatch = useDispatch();
  const storageKey = "lateral_menu_" + profile;
  
  // Obtener el estado del usuario actual desde Redux
  const currentUser = useSelector(state => {
    if (profile === 'associate') return state.app.superAdmin?.associate;
    return state.app[profile];
  });
  const userId = currentUser?.id;
  
  // Estados locales
  const [lateralMenu, setLateralMenu] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved === null) return false;

    try {
      return normalizeSidebarPinned(JSON.parse(saved));
    } catch (error) {
      return normalizeSidebarPinned(saved);
    }
  });
  const [loading, setLoading] = useState(false);
  const [synced, setSynced] = useState(false);

  // Mapeo de acciones según el perfil (memoizado)
  const getProfileActions = useCallback(() => {
    switch (profile) {
      case 'user':
        return userActions;
      case 'employee':
        return employeeActions;
      case 'driver':
        return driverActions;
      case 'client':
        return clientActions;
      case 'supplier':
        return supplierActions;
      case 'associate':
        const associateActions = require('store/actions/associates').default;
        return associateActions;
      default:
        return userActions;
    }
  }, [profile]);

  // Escuchar cambios de otras instancias del hook (sincronización en tiempo real)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail.profile === profile) {
        setLateralMenu(e.detail.value);
      }
    };
    window.addEventListener(LATERAL_MENU_EVENT, handler);
    return () => window.removeEventListener(LATERAL_MENU_EVENT, handler);
  }, [profile]);

  // Cargar estado desde el backend al montar el componente
  useEffect(() => {
    const loadSidebarState = async () => {
      if (!userId || synced) return;

      // Super admin (associate) profile doesn't have sidebar_pinned endpoint - skip API call entirely
      if (profile === 'associate') {
        setSynced(true);
        return;
      }

      // Verificar que existe token del perfil correcto antes de llamar al API
      const tokenKey = TOKEN_KEYS_BY_PROFILE[profile] || '@token';
      const hasToken = !!localStorage.getItem(tokenKey);
      if (!hasToken) {
        console.warn(`No token found for profile ${profile}, skipping sidebar API call`);
        setSynced(true);
        return;
      }
      
      setLoading(true);
      try {
        const actions = getProfileActions();
        const response = await dispatch(actions.getSidebarPinned(userId));
        
        // Handle null response (endpoint doesn't exist for this profile)
        if (response === null) {
          setSynced(true);
          return;
        }
        
        if (response && typeof response.sidebar_pinned === 'boolean') {
          setLateralMenu(response.sidebar_pinned);
          localStorage.setItem(storageKey, JSON.stringify(response.sidebar_pinned));
        }
        // If response is null or sidebar_pinned is null, just use localStorage value
        setSynced(true);
      } catch (error) {
        console.warn(`Error loading sidebar state for ${profile}:`, error);
        setSynced(true);
      } finally {
        setLoading(false);
      }
    };

    loadSidebarState();
  }, [userId, profile, dispatch, storageKey, synced, getProfileActions]);

  // Función para actualizar el estado tanto local como en el backend
  const updateLateralMenu = async (newValue) => {
    const sidebarPinned = normalizeSidebarPinned(newValue);

    // Actualizar estado local inmediatamente
    setLateralMenu(sidebarPinned);
    localStorage.setItem(storageKey, JSON.stringify(sidebarPinned));

    // Notificar a TODAS las instancias del hook (sincronización en tiempo real)
    window.dispatchEvent(new CustomEvent(LATERAL_MENU_EVENT, {
      detail: { profile, value: sidebarPinned }
    }));

    // Sincronizar con el backend
    if (userId) {
      const actions = getProfileActions();
      await dispatch(actions.updateSidebarPinned(userId, sidebarPinned));
    }
  };

  return { 
    lateralMenu, 
    setLateralMenu: updateLateralMenu,
    loading,
    synced 
  };
}
